import { useCallback, useMemo, useRef, useState } from 'react';
import { useLiveRooms } from './useLive';
import { useAuth } from '../auth/AuthProvider';

/**
 * A vehicle list that keeps itself current.
 *
 * Takes the REST snapshot and folds live socket events on top of it, so the
 * dashboard map, the vehicle grid and the fleet counters all move without
 * re-fetching. Written once and shared, because "merge a position event into
 * a row" implemented separately on three screens is three chances to get the
 * merge subtly different.
 *
 * Which rooms to join depends on how the caller is scoped: a viewer only holds
 * individual vehicles and is refused fleet rooms, so they subscribe per
 * vehicle. Everyone else joins the fleet rooms, which carry position-only
 * updates rather than the full packet — a 500-vehicle map does not need every
 * sensor field for every vehicle.
 */
export function useFleetLive(vehicles) {
  const { scope } = useAuth();
  const [overrides, setOverrides] = useState({});
  const [lastEventAt, setLastEventAt] = useState(null);
  const pending = useRef(null);

  const isVehicleScoped = scope?.vehicleCount !== null && scope?.vehicleCount !== undefined;

  const rooms = useMemo(() => {
    if (!vehicles?.length) return [];
    if (isVehicleScoped) return vehicles.map((v) => `vehicle:${v.id}`);
    const fleetIds = new Set(vehicles.map((v) => v.fleet_id).filter(Boolean));
    return [...fleetIds].map((id) => `fleet:${id}`);
  }, [vehicles, isVehicleScoped]);

  /**
   * Position events arrive per vehicle at up to 1 Hz each. Applying them one
   * setState at a time would re-render the whole grid hundreds of times a
   * second on a large fleet; batching to one frame keeps it smooth.
   */
  const queue = useCallback((id, patch) => {
    pending.current = { ...(pending.current || {}), [id]: { ...(pending.current?.[id] || {}), ...patch } };
    if (pending.current.__scheduled) return;
    pending.current.__scheduled = true;

    requestAnimationFrame(() => {
      const batch = pending.current || {};
      delete batch.__scheduled;
      pending.current = null;
      setOverrides((current) => {
        const next = { ...current };
        for (const [vehicleId, patchValue] of Object.entries(batch)) {
          next[vehicleId] = { ...(next[vehicleId] || {}), ...patchValue };
        }
        return next;
      });
      setLastEventAt(Date.now());
    });
  }, []);

  useLiveRooms(rooms, {
    'vehicle:position': (event) => {
      if (!event?.vehicleId) return;
      queue(event.vehicleId, {
        last_lat: event.positionValid ? event.lat : undefined,
        last_lon: event.positionValid ? event.lon : undefined,
        last_speed_kph: event.speedKph,
        last_heading_deg: event.headingDeg,
        last_packet_at: event.at,
        vehicle_state: event.vehicleState,
        health_score: event.healthScore,
        online: true,
      });
    },
    // A viewer joins vehicle rooms, which carry the full packet instead.
    'telemetry:update': (event) => {
      if (!event?.vehicleId) return;
      const p = event.payload || {};
      queue(event.vehicleId, {
        last_lat: p.positionValid ? p.lat : undefined,
        last_lon: p.positionValid ? p.lon : undefined,
        last_speed_kph: p.speedKph,
        last_heading_deg: p.headingDeg,
        last_packet_at: p.time,
        vehicle_state: p.vehicleState,
        health_score: p.healthScore,
        online: true,
      });
    },
    'device:offline': (event) => {
      if (event?.vehicleId) queue(event.vehicleId, { online: false, vehicle_state: 'no_signal' });
    },
    'device:online': (event) => {
      if (event?.vehicleId) queue(event.vehicleId, { online: true });
    },
  });

  const merged = useMemo(() => {
    if (!vehicles) return [];
    if (!Object.keys(overrides).length) return vehicles;
    return vehicles.map((vehicle) => {
      const patch = overrides[vehicle.id];
      if (!patch) return vehicle;
      // `undefined` in a patch means "keep what we had" — an invalid GNSS fix
      // must not erase the last known position and drop the marker off the map.
      const next = { ...vehicle };
      for (const [key, value] of Object.entries(patch)) {
        if (value !== undefined) next[key] = value;
      }
      return next;
    });
  }, [vehicles, overrides]);

  return { vehicles: merged, lastEventAt };
}
